import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Heart, Apple } from "lucide-react";
import { BabyMilestones } from "./pregnancy/BabyMilestones";
import { FeedingSleepTracker } from "./pregnancy/FeedingSleepTracker";
import { PregnancyJournal } from "./pregnancy/PregnancyJournal";
import { PregnancyInsights } from "./pregnancy/PregnancyInsights";
import { BabyProfileSetup } from "./pregnancy/BabyProfileSetup";
import { TherappiSupportCard } from "./journey/TherappiSupportCard";
import { NutritionalGuidanceCard } from "./journey/NutritionalGuidanceCard";
import TherapyTokenGift from "./cycle/TherapyTokenGift";
import AppointmentManager from "./cycle/AppointmentManager";

interface PregnancyPostpartumTrackerProps {
  onBack: () => void;
}

export default function PregnancyPostpartumTracker({ onBack }: PregnancyPostpartumTrackerProps) {
  const [selectedBabyId, setSelectedBabyId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold">Pregnancy & Postpartum Tracker</h2>
          <p className="text-muted-foreground">Track your baby's development and your postpartum journey</p>
        </div>
      </div>

      <Card className="p-6 bg-primary/5 border-primary/20">
        <h3 className="font-semibold mb-2">Why Track This Journey?</h3>
        <p className="text-sm text-muted-foreground">
          This tracker helps you capture precious moments, understand patterns in feeding and sleep, 
          and create a beautiful record of your baby's development. By tracking milestones and daily 
          rhythms, you'll gain insights into your baby's needs, celebrate growth, and have a journal 
          to look back on. This isn't just data-it's your family's story unfolding.
        </p>
      </Card>

      <TherapyTokenGift journeyType="pregnancy" />

      <BabyProfileSetup onBabySelect={setSelectedBabyId} />

      {selectedBabyId && (
        <Tabs defaultValue="milestones" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="feeding-sleep">Feed & Sleep</TabsTrigger>
            <TabsTrigger value="journal">Journal</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="support">
              <Heart className="h-4 w-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="nutrition">
              <Apple className="h-4 w-4 mr-2" />
              Nutrition
            </TabsTrigger>
          </TabsList>

          <TabsContent value="milestones" className="space-y-4">
            <BabyMilestones babyId={selectedBabyId} />
          </TabsContent>

          <TabsContent value="feeding-sleep" className="space-y-4">
            <FeedingSleepTracker babyId={selectedBabyId} />
          </TabsContent>

          <TabsContent value="journal" className="space-y-4">
            <PregnancyJournal babyId={selectedBabyId} />
          </TabsContent>

          <TabsContent value="insights" className="space-y-4">
            <PregnancyInsights babyId={selectedBabyId} />
            <AppointmentManager journeyType="pregnancy" />
          </TabsContent>

          <TabsContent value="support" className="space-y-4">
            <TherappiSupportCard journeyType="pregnancy" />
            <div className="mt-6">
              <TherappiSupportCard journeyType="postpartum" />
            </div>
          </TabsContent>

          <TabsContent value="nutrition" className="space-y-4">
            <NutritionalGuidanceCard focusArea="pregnancy" />
            <div className="mt-6">
              <NutritionalGuidanceCard focusArea="postpartum" />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
